import { alpha, Box, ButtonBase, Typography } from "@mui/material";
import { FC, ReactNode } from "react";

export type AuthMethodOption = {
  key: string;
  label: string;
  icon: ReactNode;
};

type PropsTypes = {
  value: string;
  onChange: (key: string) => void;
  methods: AuthMethodOption[];
};

/**
 * Сегментированный переключатель способа входа (Телефон / Email / Telegram).
 * Самодостаточный, переиспользуемый. Стилизован под emerald-тему,
 * работает в светлой и тёмной темах.
 */
const AuthMethodSwitcher: FC<PropsTypes> = ({ value, onChange, methods }) => {
  return (
    <Box
      role="tablist"
      aria-label="Способ входа"
      sx={{
        display: "grid",
        gridTemplateColumns: `repeat(${methods.length}, 1fr)`,
        gap: 0.5,
        p: 0.5,
        borderRadius: "12px",
        backgroundColor: (t) =>
          t.palette.mode === "dark"
            ? alpha(t.palette.common.white, 0.04)
            : alpha(t.palette.common.black, 0.03),
        border: (t) => `1px solid ${t.palette.divider}`,
      }}
    >
      {methods.map((method) => {
        const isActive = method.key === value;

        return (
          <ButtonBase
            key={method.key}
            role="tab"
            aria-selected={isActive}
            onClick={() => onChange(method.key)}
            disableRipple
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.75,
              minHeight: 40,
              px: 1,
              borderRadius: "9px",
              transition: "all 0.2s ease",
              color: isActive ? "primary.main" : "text.secondary",
              fontWeight: isActive ? 600 : 500,
              backgroundColor: (t) =>
                isActive ? alpha(t.palette.primary.main, 0.12) : "transparent",
              boxShadow: (t) =>
                isActive
                  ? `inset 0 0 0 1px ${alpha(t.palette.primary.main, 0.24)}`
                  : "none",
              "&:hover": {
                backgroundColor: (t) =>
                  isActive
                    ? alpha(t.palette.primary.main, 0.16)
                    : alpha(t.palette.primary.main, 0.06),
                color: isActive ? "primary.main" : "text.primary",
              },
              "&.Mui-focusVisible": {
                boxShadow: (t) => `0 0 0 3px ${alpha(t.palette.primary.main, 0.22)}`,
              },
              "& .MuiSvgIcon-root": {
                fontSize: 18,
                color: "inherit",
              },
            }}
          >
            {method.icon}
            <Typography
              component="span"
              sx={{
                fontSize: 13.5,
                fontWeight: "inherit",
                color: "inherit",
                lineHeight: 1,
              }}
            >
              {method.label}
            </Typography>
          </ButtonBase>
        );
      })}
    </Box>
  );
};

export default AuthMethodSwitcher;
